import { MutableRefObject, ReactElement, useLayoutEffect, useState } from 'react';
import { useEventListener } from './useEventListener';
import useWindowDimensions from './useWindowDimensions';

export type RelativePosition =
    | 'top'
    | 'top right'
    | 'right'
    | 'bottom right'
    | 'bottom'
    | 'bottom left'
    | 'left'
    | 'top left';

const useRelativeCoordsAndSize = (
    rootRef: MutableRefObject<HTMLDivElement | null>,
    position: RelativePosition,
    scrollElement?: HTMLElement | null,
) => {
    const windowSize = useWindowDimensions();

    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({ width: 0, height: 0 });

    if (scrollElement)
        useEventListener(scrollElement, 'scroll', () => positionRoot(rootRef, position));

    const positionRoot = (
        rootRef: MutableRefObject<HTMLDivElement | null>,
        position: RelativePosition,
    ) => {
        const current = rootRef.current;
        if (current == null) return;

        const { width, height, left, top } = current.getBoundingClientRect();

        const coords = { x: left, y: top };
        switch (position) {
            case 'top':
                coords.x += width / 2;
                break;
            case 'top right':
                coords.x += width;
                break;
            case 'right':
                coords.x += width;
                coords.y += height / 2;
                break;
            case 'bottom right':
                coords.x += width;
                coords.y += height;
                break;
            case 'bottom':
                coords.x += width / 2;
                coords.y += height;
                break;
            case 'bottom left':
                coords.y += height;
                break;
            case 'left':
                coords.y += height / 2;
                break;
        }

        setCoords(coords);
        setSize({ width, height });
    };

    useLayoutEffect(() => positionRoot(rootRef, position), [rootRef, position, windowSize]);

    return { coords, size };
};

export default useRelativeCoordsAndSize;
